import { useNavigate } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";
import { seoArticles } from "@/mocks/seo-articles";
import type { SEOArticleContent } from "@/mocks/seo-article-content";

interface SEOArticlePageProps {
  article: SEOArticleContent;
}

export default function SEOArticlePage({ article }: SEOArticlePageProps) {
  const navigate = useNavigate();

  const relatedArticles = seoArticles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "datePublished": article.date,
          "description": article.intro.replace(/<[^>]*>/g, ""),
          "inLanguage": "fr",
          "publisher": {
            "@type": "Organization",
            "name": "hipobuyspreadsheet.ink",
            "url": import.meta.env.VITE_SITE_URL
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": import.meta.env.VITE_SITE_URL + "/" + article.slug
          }
        })}
      </script>

      {/* Header */}
      <header className="w-full bg-background-100 py-8 md:py-12 px-4 md:px-8 border-b border-background-200/60">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            onClick={() => { navigate("/"); window.scrollTo(0, 0); }}
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors mb-4 cursor-pointer font-body"
          >
            <i className="ri-arrow-left-line" />
            Retour à l'accueil
          </button>
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight font-heading">
            {article.title}
          </h1>
          <p className="text-sm text-foreground-500 mt-2 font-body">Publié le {article.date} | Mis à jour : {article.date}</p>
        </div>
      </header>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="prose max-w-none">
          {/* Intro */}
          <p
            className="text-base md:text-lg text-foreground-800 leading-relaxed mb-8 font-body"
            dangerouslySetInnerHTML={{ __html: article.intro }}
          />

          {/* Sections */}
          {article.sections.map((section) => (
            <div key={section.id}>
              <h2 id={section.id} className="text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4 font-heading">
                {section.heading}
              </h2>
              {section.paragraphs.map((p, pIdx) => (
                <p
                  key={pIdx}
                  className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 font-body"
                  dangerouslySetInnerHTML={{ __html: p }}
                />
              ))}
            </div>
          ))}

          {/* Table */}
          {article.table && (
            <div className="mt-8 mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4 font-heading">
                Tableau : {article.table.caption}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-background-200/60 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-primary-500 text-white">
                      {article.table.headers.map((header, hIdx) => (
                        <th key={hIdx} className="px-4 py-3 text-left text-sm font-semibold font-body whitespace-nowrap">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-sm font-body">
                    {article.table.rows.map((row, rIdx) => (
                      <tr key={rIdx} className={`border-b border-background-200/40 ${rIdx % 2 === 0 ? "bg-background-50" : "bg-background-100"}`}>
                        {row.map((cell, cIdx) => (
                          <td key={cIdx} className={`px-4 py-3 ${cIdx === 0 ? "text-foreground-800 font-semibold" : "text-foreground-700"}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Conclusion */}
          <h2 id="conclusion" className="text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4 font-heading">
            Conclusion
          </h2>
          <p
            className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8 font-body"
            dangerouslySetInnerHTML={{ __html: article.conclusion }}
          />

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center gap-4 p-6 md:p-8 bg-primary-100 rounded-2xl border border-primary-200/50 mb-10">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-foreground-900 mb-1 font-heading">Prêt à commencer avec Hipobuy Spreadsheet ?</h3>
              <p className="text-sm text-foreground-600 font-body">Accédez à la plateforme complète et découvrez tous les outils.</p>
            </div>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer font-body shrink-0"
            >
              <span>Buy It Now</span>
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>

        {/* FAQ */}
        {article.faqs.length > 0 && (
          <div className="mt-14 md:mt-16" itemScope itemType="https://schema.org/FAQPage">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
              Questions fréquemment posées
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq, fIdx) => (
                <div
                  key={fIdx}
                  className="border border-background-200/60 rounded-xl p-5 md:p-6 bg-white"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-body" itemProp="name">
                    {faq.q}
                  </h3>
                  <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="text-sm text-foreground-700 leading-relaxed font-body" itemProp="text">
                      {faq.a}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related Articles */}
        <div className="mt-14 md:mt-16">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
            Articles connexes
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {relatedArticles.map((related) => (
              <button
                key={related.slug}
                type="button"
                onClick={() => { navigate(`/${related.slug}`); window.scrollTo(0, 0); }}
                className="group flex gap-4 p-4 bg-white rounded-xl border border-background-200/60 hover:border-primary-300 hover:shadow transition-all cursor-pointer text-left"
              >
                <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0">
                  <img
                    src={related.image}
                    alt={related.title}
                    className="w-full h-full object-cover object-top"
                    width={80}
                    height={80}
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <span className="text-xs text-foreground-400 font-body mb-1">{related.date}</span>
                  <span className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors font-body line-clamp-2">
                    {related.title}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-10 pt-6 border-t border-background-200/60">
          <button
            type="button"
            onClick={() => { navigate("/"); window.scrollTo(0, 0); }}
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer font-body"
          >
            <i className="ri-home-4-line" />
            <span>Retour à la page d'accueil Hipobuy Spreadsheet</span>
          </button>
        </div>
      </article>

      {/* Footer */}
      <footer className="w-full bg-foreground-900 text-white py-8 px-4 md:px-8 mt-10">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/50 font-body">© 2026 hipobuyspreadsheet.ink - Tous droits réservés</p>
          <div className="flex gap-4 flex-wrap justify-center">
            <button type="button" onClick={() => { navigate("/"); window.scrollTo(0, 0); }} className="text-xs text-white/50 hover:text-white transition-colors cursor-pointer font-body">
              Accueil
            </button>
            {seoArticles.slice(0, 5).map((a) => (
              <button
                key={a.slug}
                type="button"
                onClick={() => { navigate(`/${a.slug}`); window.scrollTo(0, 0); }}
                className="text-xs text-white/50 hover:text-white transition-colors cursor-pointer font-body"
              >
                {a.title.length > 35 ? a.title.slice(0, 35) + "..." : a.title}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}